import {
  DEFAULT_AXIS_LINE_COLOUR,
  DEFAULT_AXIS_SPLIT_NUMBER,
  DEFAULT_LARGE_SERIES_THRESHOLD,
  SERIES_COLLECTION_DATA_COUNT_THRESHOLD
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { AxisType } from "types/echarts";
import { EvaChartStates } from "types/factory";

import { getCountOfSeriesCollectionData } from "components/charts/utils";

import { createChartLocks } from "../locks";
import { formatChartTitleWithSubscript, getLineSeriesMinMax } from "./utils";

export function translateMaterialBalanceTimeResponseToOptions(
  states: Partial<EvaChartStates>
) {
  const { title, series, layout, overrideSeries = true } = states;

  if (series.length === 0) {
    return {};
  }

  const { xMin, xMax, yMin, yMax } = getLineSeriesMinMax(series);
  const seriesCollectionDataCount = getCountOfSeriesCollectionData(series, "x");
  const enableHighlight =
    seriesCollectionDataCount < SERIES_COLLECTION_DATA_COUNT_THRESHOLD;

  const { lockedXMax, lockedXMin, lockedYMax, lockedYMin } = createChartLocks({
    type: EvaChart.MaterialBalanceTime,
    states
  });
  const yAxisTitle = layout?.yAxis?.title || "";

  // Process labels from the layout
  const labels = {};
  for (const kvp of layout.yAxis.labels) {
    labels[kvp.value] = kvp.label;
  }

  // Compute series only if api response is changed
  let computedSeries;
  if (overrideSeries) {
    computedSeries = series.map((s, seriesIndex) => {
      const { label, style, x, y, dates } = s;
      // Check if there's any data to process
      if (x.length === 0 || y.length === 0) {
        return null;
      }
      const len = x.length;
      const newSeriesData = new Array(len);

      // Process each data point in a single loop
      for (let i = 0; i < len; i++) {
        // Use a default of 0 if dates is not provided or if the data point is undefined
        const date = dates && dates[i] !== undefined ? dates[i] : 0;
        newSeriesData[i] = [
          x[i], // x value
          y[i], // y value
          label, // series name
          `${seriesIndex}-${i}`, // unique identifier
          date // production date
        ];
      }

      return {
        ...createEntityState(ChartOption.ScatterSeries, {
          id: `${label}, ${seriesIndex}`,
          name: label,
          data: newSeriesData,
          itemStyle: {
            color: s.isForecast ? "none" : style.hexColor,
            borderColor: s.isForecast ? style.hexColor : "none",
            opacity: 1
          },
          largeThreshold:
            seriesCollectionDataCount > SERIES_COLLECTION_DATA_COUNT_THRESHOLD
              ? 1
              : DEFAULT_LARGE_SERIES_THRESHOLD
        })
      };
    });
  }

  const options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.MaterialBalanceTime,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        name: formatChartTitleWithSubscript(yAxisTitle),
        nameGap: 45,
        triggerEvent: true,
        nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
        min: lockedYMin ?? 0.01,
        max: lockedYMax ?? null,
        type: "log",
        nameLocation: "middle",
        yAxisScaleToX: layout.yAxis.yScaleToX,
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: "rgb(155,155,155)", width: 1 }
        },
        splitLine: { lineStyle: { color: "#D9D9D9" } },
        axisTick: { show: true, lineStyle: { width: 1 } },
        axisPointer: { label: { margin: 3 } },
        axisLabel: {
          fontWeight: 500,
          color: "#5B6770",
          fontSize: 12,
          margin: 8,
          showMinLabel: true
        },
        minorSplitLine: { show: true, lineStyle: { color: "#F2F2F2" } },
        minorTick: { show: false, splitNumber: 9 },
        lock: {
          min: { value: Math.floor(yMin.value), type: xMin.type },
          max: { value: Math.floor(yMax.value), type: xMax.type },
          show: true
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        name: formatChartTitleWithSubscript(layout.xAxis.title),
        nameGap: 27,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: AxisType.Log,
        min: lockedXMin ?? 0.01,
        max: lockedXMax ?? null,
        minorTick: {
          show: false,
          splitNumber: DEFAULT_AXIS_SPLIT_NUMBER
        },
        minorSplitLine: {
          show: true,
          lineStyle: { color: DEFAULT_AXIS_LINE_COLOUR }
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          min: { value: Math.floor(xMin.value), type: xMin.type },
          max: { value: Math.floor(xMax.value), type: xMax.type }
        },
        useY: true
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    ...(overrideSeries && { series: computedSeries }),
    enableHighlight
  });

  return options;
}
